import React, { useEffect, useRef, useState } from 'react';
import { useForm, useWatch, Controller } from 'react-hook-form';
import ExamModuleList from '../../../component/EliteGame/ExamModuleList';
import {
  deleteExamModule,
  GetExamModule,
  PostExamModule,
  PutExamModule
} from '../../../api/eliteGameApi';
import { errorToast, successToast } from '../../../utils/helper';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { useEliteGames } from '../../../context/EliteGamesContext';
import DeletePopup from '../../../component/common/modals/DeletePopup';
import moment from 'moment';

const ExamModule = () => {
  const [page, setPage] = useState(1);
  const [examModules, setExamModules] = useState([]);
  const [filters, setFilters] = useState({});
  const [selectedExam, setSelectedExam] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false); // ✅ Calendar toggle
  const [examId, setExamId] = useState(null);
  const [open, setOpen] = useState(false);

  const { setLoading } = useEliteGames();
  const formRef = useRef(null); // Ref for scrolling
  const {
    register,
    setValue,
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm();

  useEffect(() => {
    register('canRechangeAnswer', {
      required: 'Please select an option'
    });
  }, [register]);

  const handleEdit = (examModule) => {
    setSelectedExam(examModule);
    setValue('language', examModule.language);
    setValue('title', examModule.title);
    setValue('date', examModule.date);
    setValue('examKey', examModule.examKey);
    setValue('duration', examModule.duration);
    setValue('canRechangeAnswer', examModule.canRechangeAnswer);
    formRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  const canRechangeAnswer = useWatch({
    control,
    name: 'canRechangeAnswer'
  });

  const fetchData = async (filters = {}) => {
    setLoading(true);
    try {
      const res = await GetExamModule({ page, ...filters });
      setExamModules(res);
      console.log('Fetched exam modules:', res.data);
    } catch (err) {
      console.error('Error fetching exams', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, filters, refresh]);

  const handleFilter = (filterObj) => {
    fetchData(filterObj);
    setPage(1);
  };

  const onSubmit = (data) => {
    if (examModules) {
      const formData = {
        ...data,
        canRechangeAnswer: canRechangeAnswer
      };
      PutExamModule(selectedExam._id, formData)
        .then((response) => {
          successToast('Exam Module updated successfully', response.message);
        })
        .catch((error) => {
          errorToast('Failed to update Exam Module', error.message);
        });
    } else {
      const formData = {
        ...data,
        canRechangeAnswer: canRechangeAnswer
      };
      PostExamModule(formData)
        .then((response) => {
          successToast('Exam Module created successfully', response.message);
        })
        .catch((error) => {
          errorToast('Failed to create Exam Module', error.message);
        });
    }
    setRefresh(!refresh);
    fetchData();
    reset();
    setSelectedExam(null);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      // Call API to delete exam module
      await deleteExamModule(examId);
      successToast('Exam Module deleted successfully');
      setRefresh(true); // Refresh questions list after deletion
    } catch (error) {
      errorToast('Error deleting exam module:', error);
    } finally {
      setLoading(false);
    }
    setRefresh(!refresh);
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow p-6 mx-4">
        <div className="cursor-pointer mb-3 text-[24px] font-bold leading-9 text-shoorah-lightBlack dark:text-shoorah-darkBg ">
          Create and Manage Exam Module
        </div>
      </div>
      <div ref={formRef}>
        <div className="bg-white rounded-lg shadow p-6 m-4">
          <h2 className="text-xl font-semibold text-black-600 mb-6">Create Exam Module</h2>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-3 gap-6 mb-6">
              {/* Language */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Language <span className="text-pink-600">*</span>
                </label>
                <select
                  className="input-company"
                  {...register('language', { required: 'Language is required' })}
                >
                  <option value="">Select Language</option>
                  <option value="english">English</option>
                  <option value="hindi">Hindi</option>
                </select>
                {errors.language && (
                  <span className="text-red-500 text-sm mt-1 block">{errors.language.message}</span>
                )}
              </div>
              {/* Title */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Title <span className="text-pink-600">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter Title"
                  className="input-company"
                  {...register('title', { required: 'Title is required' })}
                />
                {errors.title && (
                  <span className="text-red-500 text-sm mt-1 block">{errors.title.message}</span>
                )}
              </div>

              {/* ✅ Updated Date with Calendar */}
              <div className="relative">
                <label className="block text-gray-700 font-medium mb-2">
                  Date <span className="text-pink-600">*</span>
                </label>
                <Controller
                  name="date"
                  control={control}
                  rules={{ required: 'Date is required' }}
                  render={({ field }) => {
                    const { value, onChange } = field;

                    return (
                      <div className="relative">
                        <input
                          type="text"
                          readOnly
                          value={value ? moment(value).format('DD/MM/YYYY') : ''}
                          onClick={() => setShowCalendar((prev) => !prev)}
                          className="input-company cursor-pointer"
                          placeholder="Select a date"
                        />
                        {showCalendar && (
                          <div className="absolute z-10 mt-2">
                            <Calendar
                              onChange={(date) => {
                                onChange(date);
                                setShowCalendar(false);
                              }}
                              value={value ? new Date(value) : null}
                            />
                          </div>
                        )}
                      </div>
                    );
                  }}
                />
                {errors.date && (
                  <span className="text-red-500 text-sm mt-1 block">{errors.date.message}</span>
                )}
              </div>
            </div>

            {/* Second row: ExamKey, Duration, Recharge Option */}
            <div className="grid grid-cols-3 gap-6">
              {/* Exam Key */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Exam Key <span className="text-pink-600">*</span>
                </label>
                <input
                  type="number"
                  className="input-company"
                  {...register('examKey', {
                    required: 'Exam Key is required',
                    min: { value: 1000, message: 'Minimum value is 1000' },
                    max: { value: 9999, message: 'Maximum value is 9999' }
                  })}
                />
                <small className="text-pink-600">(Minimum 1000 and Maximum 9999)</small>
                {errors.examKey && (
                  <span className="text-red-500 text-sm mt-1 block">{errors.examKey.message}</span>
                )}
              </div>

              {/* Duration */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Duration (in minutes) <span className="text-pink-600">*</span>
                </label>
                <input
                  type="number"
                  className="input-company"
                  {...register('duration', {
                    required: 'Duration is required',
                    min: { value: 1, message: 'Minimum value is 1' },
                    max: { value: 180, message: 'Maximum value is 180' }
                  })}
                />
                <small className="text-pink-600">(Minimum 1 and Maximum 180)</small>
                {errors.duration && (
                  <span className="text-red-500 text-sm mt-1 block">{errors.duration.message}</span>
                )}
              </div>

              {/* Can Recharge Answer */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Can rechange answer while in exam <span className="text-pink-600">*</span>
                </label>
                <div className="flex items-center space-x-4">
                  <button
                    type="button"
                    onClick={() => setValue('canRechangeAnswer', true)}
                    className={`px-3 py-1 rounded ${
                      canRechangeAnswer === true ? 'bg-green-700 text-white' : 'bg-gray-200'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setValue('canRechangeAnswer', false)}
                    className={`px-3 py-1 rounded ${
                      canRechangeAnswer === false ? 'bg-red-700 text-white' : 'bg-gray-200'
                    }`}
                  >
                    No
                  </button>
                </div>
                {canRechangeAnswer === null && (
                  <span className="text-red-500 text-sm mt-1 block">Please select an option</span>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="text-center mt-6">
              <button
                type="submit"
                className="btn-primary w-full sm:w-auto mt-0 sm:mt-7 sm:ms-auto block"
              >
                {selectedExam ? 'Update Exam Module' : 'Create Exam Module'}
              </button>
            </div>
          </form>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow p-6 mx-4">
        <ExamModuleList
          data={examModules}
          page={page}
          setPage={setPage}
          onFilter={handleFilter}
          onEdit={handleEdit}
          onDelete={(id) => {
            setExamId(id);
            setOpen(true);
          }}
        />
        {DeletePopup({
          title: 'Confirm Deletion',
          open,
          setOpen,
          message:
            'Are you sure you want to delete this exam module? All related data will also be deleted.',
          setDelete: handleDelete
        })}
      </div>
    </>
  );
};

export default ExamModule;
